/* eslint-disable no-use-before-define, no-unused-vars, react/prop-types */
import _ from 'underscore';
import React from 'react';
import {
  Accordion,
  Alert,
  FormControl,
  ControlLabel,
  Button as BsButton,
  Radio,
  Checkbox,
  Glyphicon as BsGlyphicon,
  Dropdown,
  DropdownButton,
  MenuItem,
  Modal,
  Carousel as BsCarousel,
  Overlay,
  OverlayTrigger,
  Tooltip,
  Popover,
  Navbar as BsNavbar,
  Nav,
  NavDropdown as UnstyledNavDropdown,
  NavItem,
  Tabs as BsTabs,
  Tab,
  Image,
} from 'react-bootstrap';
import { ICarouselInstance } from './types';

type INavbarComponent = React.ForwardRefExoticComponent<NavbarProps & React.RefAttributes<HTMLDivElement>> & {
  Brand: typeof BsNavbar.Brand;
};

// Button Component

type ButtonProps = React.ComponentProps<typeof BsButton> & {
  btnStyle?: string;
  className?: string;
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { btnStyle, className, ...other } = props;
  let bsStyle = btnStyle;
  let btnClasses = className;

  // For CommandBar button style, add btn-command class
  if (btnStyle === 'command') {
    const btnClass = ['btn-command'];

    if (btnClasses) {
      btnClass.push(btnClasses);
    }
    btnClasses = className ? btnClass.join(' ') : btnClass.join('');
    bsStyle = 'default';
  }

  return (<BsButton {...other} bsStyle={bsStyle} className={btnClasses} ref={ref as any} />);
});

Button.defaultProps = {
  btnStyle: undefined,
  className: undefined,
};

// Glyphicon Component

type GlyphiconProps = {
  bsClass?: string;
  className?: string;
  glyph: string;
  iconStyle?: string;
};

const Glyphicon = React.forwardRef<HTMLSpanElement, GlyphiconProps>((props, ref) => {
  const {
    iconStyle,
    className,
    glyph,
    bsClass,
    ...other
  } = props;
  let iconGlyph = glyph;
  let iconBsClass = bsClass;
  let iconClasses = className;

  // For bingads specific icons (such as ba-chart), trim ba- in glyph prop and add custom bsClass
  if (iconGlyph && iconGlyph.indexOf('ba-') === 0) {
    const bsClasses = ['iconba'];

    if (iconBsClass) {
      bsClasses.push(iconBsClass);
    }
    iconBsClass = bsClasses.join(' ');
    iconGlyph = iconGlyph.substr(3);
  }

  if (iconStyle) {
    const iconClass = ['icon-'];
    iconClass[0] += iconStyle;

    if (iconClasses) {
      iconClass.push(iconClasses);
    }
    iconClasses = className ? iconClass.join(' ') : iconClass.join('');
  } else {
    iconClasses = className;
  }

  if (iconClasses) {
    iconClasses = iconClasses.trim();
  }

  return (
    <BsGlyphicon
      {...other}
      bsClass={iconBsClass}
      className={iconClasses}
      glyph={iconGlyph}
      ref={ref as any}
    />
  );
});

Glyphicon.defaultProps = {
  bsClass: undefined,
  className: undefined,
  iconStyle: undefined,
};

// Input Component

type InputProps = React.ComponentProps<typeof FormControl> & {
  isStatic?: boolean;
  staticDisplay?: (value: React.ComponentProps<typeof FormControl>['value']) => React.ReactNode;
  type?: string;
  value?: string;
};

const Input = React.forwardRef<React.ComponentRef<typeof FormControl> | React.ComponentRef<typeof FormControl.Static>, InputProps>((props, ref) => {
  const { isStatic, staticDisplay, ...rest } = props;
  return isStatic
    ? (
      <FormControl.Static
        componentClass="span"
        {...rest}
        type={rest.type}

        ref={ref as React.Ref<React.ComponentRef<typeof FormControl.Static>>}
      >
        {staticDisplay && staticDisplay(props.value)}
      </FormControl.Static>
    )
    : (
      <FormControl
        {...rest}
        type={rest.type}

        ref={ref as React.Ref<React.ComponentRef<typeof FormControl>>}
      />
    );
});

Input.defaultProps = {
  isStatic: false,
  staticDisplay: _.identity,
  type: 'text',
  value: undefined,
};

// Navbar Component

type NavbarProps = React.ComponentProps<typeof BsNavbar> & {
  // eslint-disable-next-line react/require-default-props
  navStyle?: string;
};

const Navbar = React.forwardRef<HTMLDivElement, NavbarProps>((props, ref) => {
  const { navStyle, className, ...other } = props;
  let navClasses = className;

  if (navStyle === 'default') {
    const navClass = ['default-toolbar'];
    navClass.push(navClasses as string);
    navClasses = className ? navClass.join(' ') : navClass.join('');
  }

  if (navStyle === 'primary') {
    const navClass = ['primary-toolbar'];
    navClass.push(navClasses as string);
    navClasses = className ? navClass.join(' ') : navClass.join('');
  }

  return (
    <BsNavbar {...other} className={navClasses} ref={ref as any}>
      {props.children}
    </BsNavbar>
  );
}) as INavbarComponent;

Navbar.defaultProps = {
  children: null,
  className: undefined,
  navStyle: 'default',
};

Navbar.Brand = BsNavbar.Brand;

// Carousel Component

type CarouselProps = React.ComponentProps<typeof BsCarousel> & {
  controls?: boolean;
  showPrevNextControls?: boolean;
  InjectedControls?: React.ReactElement;
};

type TabsProps = React.ComponentProps<typeof BsTabs> & {
  transition?: boolean;
};

const Tabs = BsTabs as React.ComponentType<TabsProps>;

class Carousel extends React.Component<CarouselProps> {
  static defaultProps = {
    controls: true,
    showPrevNextControls: true,
    InjectedControls: null as React.ReactElement | null,
  };

  constructor(props: CarouselProps) {
    super(props);
    this.carouselRef = React.createRef<ICarouselInstance>();
  }

  carouselRef: React.RefObject<ICarouselInstance>;

  static Item = BsCarousel.Item;

  static Caption = BsCarousel.Caption;

  handlePause = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (this.carouselRef.current) {
      this.carouselRef.current.pause();
    }
  };

  handlePlay = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (this.carouselRef.current) {
      this.carouselRef.current.play();
    }
  };

  render() {
    const {
      controls,
      showPrevNextControls,
      InjectedControls, ...other
    } = this.props;

    return (
      <div className="carousel-wrapper">
        <BsCarousel ref={this.carouselRef as React.RefObject<any>} controls={showPrevNextControls} {...other}>
          {this.props.children}
        </BsCarousel>
        {!!controls && (
        <div className="carousel-controls">
          <button id="playButton" onClick={this.handlePlay}>Play</button>
          <button id="pauseButton" onClick={this.handlePause}>Pause</button>
        </div>
        )}
        {InjectedControls
          ? (
            <>
              {InjectedControls}
            </>
          )
          : null}
      </div>
    );
  }
}

export {
  Accordion,
  Alert,
  ControlLabel as Label,
  Button,
  Radio,
  Input,
  Checkbox,
  Glyphicon,
  Dropdown,
  DropdownButton,
  MenuItem,
  Modal,
  Carousel,
  Overlay,
  OverlayTrigger,
  Tooltip,
  Popover,
  Navbar,
  Nav,
  UnstyledNavDropdown as NavDropdown,
  NavItem,
  Tabs,
  Tab,
  Image,
};

export const Select = React.forwardRef<HTMLSelectElement, React.ComponentProps<typeof FormControl>>((props, ref) => (<FormControl {...props} componentClass="select" ref={ref as any} />));

export const TextArea = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<typeof FormControl>>((props, ref) => (<FormControl {...props} componentClass="textarea" ref={ref as any} />));
